// src/components/Cookies.jsx
import { getUserLanguage } from '../utils/language';
import Navbar from '../components/common/NavBar';
import MiniHero from '../components/common/MiniHero';
import Footer from '../components/common/Footer';

const Cookies = () => {
  const language = getUserLanguage();

  const texts = {
    cs: {
      title: 'Nastavení cookies',
      intro:
        'Tato stránka používá cookies k zajištění funkčnosti, analýze návštěvnosti a personalizaci obsahu. Níže naleznete informace o cookies a možnosti jejich správy.',
      sections: {
        intro: {
          title: '1. Co jsou cookies?',
          content:
            'Cookies jsou malé textové soubory, které webové stránky ukládají do vašeho zařízení. Pomáhají nám zlepšovat funkčnost webu NO RISK NO DRIFT, analyzovat jeho využití a přizpůsobovat obsah vašim preferencím.',
        },
        types: {
          title: '2. Typy cookies, které používáme',
          content:
            'a) **Nezbytné cookies**: Nutné pro základní fungování webu a e-shopu (např. přihlášení, nákupní košík). Tyto cookies nelze deaktivovat.\n' +
            'b) **Analytické cookies**: Slouží k analýze návštěvnosti (např. Google Analytics) a pomáhají nám pochopit, jak web používáte.\n' +
            'c) **Marketingové cookies**: Používáme je k personalizaci obsahu a reklam (např. na sociálních sítích), pokud s tím souhlasíte.',
        },
        purpose: {
          title: '3. Účel použití cookies',
          content:
            'Cookies používáme k zajištění plynulého provozu webu, zlepšení uživatelského zážitku, měření výkonu stránek a případně k cílení relevantního obsahu. Nezbytné cookies jsou aktivní vždy, ostatní typy vyžadují váš souhlas.',
        },
        management: {
          title: '4. Správa cookies',
          content:
            'Své preference můžete upravit v cookie banneru, který se zobrazí při první návštěvě webu. Kdykoliv můžete změnit nastavení v prohlížeči nebo v patičce webu kliknutím na "Nastavení cookies". Odmítnutí nepotřebných cookies neovlivní základní funkčnost webu.',
        },
        thirdParties: {
          title: '5. Třetí strany',
          content:
            'Některé cookies pocházejí od třetích stran (např. Google Analytics, Facebook Pixel), které nám pomáhají analyzovat data nebo zobrazovat reklamy. Tyto služby jsou aktivní pouze s vaším souhlasem a jejich zásady ochrany dat naleznete na jejich stránkách.',
        },
        legal: {
          title: '6. Právní základ',
          content:
            'Použití nezbytných cookies vychází z našeho oprávněného zájmu na provozu webu. Analytické a marketingové cookies zpracováváme na základě vašeho souhlasu podle čl. 6 odst. 1 písm. a) GDPR. Souhlas můžete kdykoliv odvolat.',
        },
        contact: {
          title: '7. Kontakt',
          content:
            'Máte-li otázky ohledně cookies, kontaktujte nás na [email z ContactSection]. Více informací o ochraně údajů naleznete v našich Zásadách ochrany osobních údajů.',
        },
      },
    },
    en: {
      title: 'Cookie Settings',
      intro:
        'This website uses cookies to ensure functionality, analyze traffic, and personalize content. Below you will find information about cookies and how to manage them.',
      sections: {
        intro: {
          title: '1. What are cookies?',
          content:
            'Cookies are small text files that websites store on your device. They help us enhance the functionality of the NO RISK NO DRIFT website, analyze its usage, and tailor content to your preferences.',
        },
        types: {
          title: '2. Types of cookies we use',
          content:
            'a) **Essential cookies**: Necessary for the basic operation of the website and e-shop (e.g., login, shopping cart). These cannot be disabled.\n' +
            'b) **Analytical cookies**: Used to analyze traffic (e.g., Google Analytics) and help us understand how you use the site.\n' +
            'c) **Marketing cookies**: Used for content and ad personalization (e.g., on social media), if you consent.',
        },
        purpose: {
          title: '3. Purpose of cookies',
          content:
            'We use cookies to ensure smooth website operation, improve user experience, measure site performance, and potentially target relevant content. Essential cookies are always active; other types require your consent.',
        },
        management: {
          title: '4. Managing cookies',
          content:
            'You can adjust your preferences in the cookie banner displayed on your first visit. You can change settings anytime in your browser or by clicking "Cookie Settings" in the website footer. Rejecting non-essential cookies will not affect core functionality.',
        },
        thirdParties: {
          title: '5. Third parties',
          content:
            'Some cookies come from third parties (e.g., Google Analytics, Facebook Pixel) to help us analyze data or display ads. These services are active only with your consent, and their privacy policies are available on their websites.',
        },
        legal: {
          title: '6. Legal basis',
          content:
            'The use of essential cookies is based on our legitimate interest in operating the website. Analytical and marketing cookies are processed with your consent under Article 6(1)(a) of the GDPR. You can withdraw consent at any time.',
        },
        contact: {
          title: '7. Contact',
          content:
            'If you have questions about cookies, contact us at [email from ContactSection]. More information about data protection is available in our Privacy Policy.',
        },
      },
    },
  };

  // Placeholder footer settings (replace with actual settings if fetched dynamically)
  const footerSettings = {
    instagram: 'https://instagram.com/norisknodrift',
    facebook: 'https://facebook.com/norisknodrift',
    youtube: 'https://youtube.com/norisknodrift',
    tiktok: 'https://tiktok.com/@norisknodrift',
  };

  const currentText = texts[language] || texts.en; // Fallback to English

  return (
    <div className="min-h-screen bg-black">
      {/* Navbar */}
      <Navbar language={language} />

      {/* MiniHero Section */}
      <MiniHero title={currentText.title} description={currentText.intro} />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-10 text-white">
        <section className="mb-6">
          <h2 className="text-2xl font-semibold mb-4">{currentText.sections.intro.title}</h2>
          <p className="text-gray-400">{currentText.sections.intro.content}</p>
        </section>
        <section className="mb-6">
          <h2 className="text-2xl font-semibold mb-4">{currentText.sections.types.title}</h2>
          <p className="text-gray-400 whitespace-pre-line">{currentText.sections.types.content}</p>
        </section>
        <section className="mb-6">
          <h2 className="text-2xl font-semibold mb-4">{currentText.sections.purpose.title}</h2>
          <p className="text-gray-400">{currentText.sections.purpose.content}</p>
        </section>
        <section className="mb-6">
          <h2 className="text-2xl font-semibold mb-4">{currentText.sections.management.title}</h2>
          <p className="text-gray-400">{currentText.sections.management.content}</p>
        </section>
        <section className="mb-6">
          <h2 className="text-2xl font-semibold mb-4">{currentText.sections.thirdParties.title}</h2>
          <p className="text-gray-400">{currentText.sections.thirdParties.content}</p>
        </section>
        <section className="mb-6">
          <h2 className="text-2xl font-semibold mb-4">{currentText.sections.legal.title}</h2>
          <p className="text-gray-400">{currentText.sections.legal.content}</p>
        </section>
        <section className="mb-6">
          <h2 className="text-2xl font-semibold mb-4">{currentText.sections.contact.title}</h2>
          <p className="text-gray-400">{currentText.sections.contact.content}</p>
        </section>
      </div>

      {/* Footer */}
      <Footer
        instagram={footerSettings.instagram}
        facebook={footerSettings.facebook}
        youtube={footerSettings.youtube}
        tiktok={footerSettings.tiktok}
      />
    </div>
  );
};

export default Cookies;
